/**
 * AutoSummaryPanel Component - Modern UI
 * Professional AI-generated report summaries with glassmorphism and modern design
 */

import React, { useState, useEffect } from 'react';
import { Button, Spin, Alert, Typography, Space, Divider, Tag, Tooltip, Collapse, Badge, Progress, message, Segmented, Select, Tabs } from 'antd';
import {
    RobotOutlined,
    ReloadOutlined,
    CloseOutlined,
    ThunderboltOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    RiseOutlined,
    WarningOutlined,
    BulbOutlined,
    CameraOutlined,
    DatabaseOutlined,
    TrophyOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DoubleRightOutlined,
    MinusOutlined,
    BarChartOutlined,
    EyeOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    FileTextOutlined,
    DashboardOutlined
} from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../../config/msalConfig';
import SummaryCard from './SummaryCard';
import TrendIndicator from './TrendIndicator';
import FeedbackWidget from './FeedbackWidget';
import { useReportSummary, SUMMARY_STATUS } from '../../hooks/useReportSummary';
import AIDashboardReport from '../AIDashboardReport';
import { generatePageDashboard, regeneratePageDashboard } from '../../services/digestService';
import execute from '../../hooks/execute';
import TemplateRenderer from './TemplateRenderer';
import TemplateFeedbackModal from './TemplateFeedbackModal';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// Animations
const pulse = keyframes`
    0%, 100% {
        transform: scale(0.8);
        opacity: 0.5;
    }
    50% {
        transform: scale(1.2);
        opacity: 1;
    }
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Modern Glassmorphism Container
const ModernPanelContainer = styled.div`
    background: white;
    border-radius: 0;
    padding: 0;
    max-width: ${props => props.expanded ? '92%' : '100%'};
    width: ${props => props.expanded ? '92%' : '100%'};
    height: 80vh;
    max-height: 80vh;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
    box-sizing: border-box;
`;

const InnerPanel = styled.div`
    background: white;
    backdrop-filter: none;
    border-radius: 0;
    padding: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
    box-sizing: border-box;
`;

const ScrollableContent = styled.div`
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px;
    min-height: 0;
    box-sizing: border-box;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, #FF7F3F 0%, #F16622 100%);
        border-radius: 10px;

        &:hover {
            background: linear-gradient(180deg, #F16622 0%, #D14D00 100%);
        }
    }
`;

// Enhanced Header Section
const ModernHeader = styled.div`
    background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%);
    color: white;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: none;
    border-bottom: 2px solid #F16622;
    position: sticky;
    top: 0;
    z-index: 10;
`;

const HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    .icon {
        font-size: 22px;
    }

    h3 {
        color: white;
        margin: 0;
        font-size: 18px;
        font-weight: 600;
    }
`;

const CloseButton = styled(Button)`
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
        color: white;
    }
`;

// Headline Section with Stats
const HeadlineSection = styled.div`
    background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%);
    color: white;
    padding: 16px;
    margin: 0 -16px 16px -16px;
    border-radius: 0;
    border-bottom: 2px solid #D14D00;
    animation: ${fadeIn} 0.5s ease;
    box-sizing: border-box;
`;

const HeadlineRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const HeadlineText = styled.h2`
    color: white;
    font-size: 17px;
    font-weight: 600;
    margin: 0;
    line-height: 1.4;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const SectionToggleBar = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 6px 0 10px;
    padding-right: 0;
`;

const QuickStats = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 12px;
    box-sizing: border-box;
    width: 100%;
`;

const StatBox = styled.div`
    background: transparent;
    border-radius: 0;
    padding: 0;
    text-align: center;
    transition: all 0.2s ease;
    border-right: 1px solid rgba(255, 255, 255, 0.2);

    &:last-child {
        border-right: none;
    }

    .label {
        font-size: 10px;
        opacity: 0.8;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin-bottom: 4px;
        font-weight: 500;
    }

    .value {
        font-size: 24px;
        font-weight: 300;
        line-height: 1;
    }
`;

// Interactive Metric Cards
const MetricCard = styled.div`
    background: transparent;
    border-radius: 0;
    padding: 14px 0;
    margin-bottom: 0;
    border-bottom: 1px solid #f0f0f0;
    transition: all 0.2s ease;
    animation: ${fadeIn} 0.5s ease;
    box-sizing: border-box;
    width: 100%;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: rgba(241, 102, 34, 0.02);
    }
`;

const MetricHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
`;

const MetricName = styled.div`
    font-weight: 600;
    font-size: 16px;
    color: #333;
    flex: 1;
`;

const MetricValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const MetricProgress = styled.div`
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
    margin-top: 6px;

    .fill {
        height: 100%;
        background: linear-gradient(90deg, #FF7F3F 0%, #F16622 100%);
        transition: width 0.8s ease;
        border-radius: 2px;
    }
`;

const TrendBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    border-radius: 0;
    font-size: 10px;
    font-weight: 500;
    margin-left: 6px;
    background: transparent;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    &.improving {
        color: #52c41a;
    }
    &.declining {
        color: #ff4d4f;
    }
    &.stable {
        color: #1890ff;
    }
`;

// Modern Collapse Sections
const StyledCollapse = styled(Collapse)`
    background: transparent;
    border: none;
    margin-top: 0;
    box-sizing: border-box;

    .ant-collapse-item {
        background: transparent;
        border-radius: 0;
        margin-bottom: 0;
        border: none;
        border-bottom: 1px solid #f0f0f0;
        overflow: hidden;
        transition: all 0.2s ease;

        &:last-child {
            border-bottom: none;
        }
    }

    .ant-collapse-header {
        padding: 16px 0 !important;
        font-weight: 600;
        font-size: 16px;
        background: transparent !important;
        transition: all 0.2s ease;
        color: #333 !important;

        &:hover {
            color: #F16622 !important;
        }

        .ant-collapse-arrow {
            color: #999 !important;
        }
    }

    .ant-collapse-content-box {
        padding: 0 0 16px 0;
        box-sizing: border-box;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .title {
        display: flex;
        align-items: center;
        gap: 6px;

        .icon {
            font-size: 15px;
        }
    }
`;

// Timeline Considerations
const Timeline = styled.div`
    position: relative;
    padding-left: 0;
    box-sizing: border-box;
    width: 100%;
`;

const TimelineItem = styled.div`
    position: relative;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f5f5f5;
    animation: ${fadeIn} 0.5s ease;
    box-sizing: border-box;
    width: 100%;

    &:last-child {
        margin-bottom: 0;
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const TimelineBadge = styled.div`
    display: inline-block;
    padding: 0;
    border-radius: 0;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 8px;
    background: transparent;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const TimelineContent = styled.div`
    background: transparent;
    padding: 0;
    border-radius: 0;
    border-left: none;
    font-size: 15px;
    line-height: 1.7;
    color: #555;
    box-sizing: border-box;
    word-wrap: break-word;
`;

// Data Source Badges
const DataSourceBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: none;
    border-radius: 0;
    font-size: 11px;
    font-weight: 500;
    margin-right: 6px;
    margin-bottom: 6px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    transition: all 0.2s ease;
    color: white;
    text-shadow: none;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    &:hover {
        transform: none;
        box-shadow: none;
        background: rgba(255, 255, 255, 0.3);
    }

    .icon {
        font-size: 13px;
        filter: none;
    }
`;

// Enhanced Insight Items
const InsightItem = styled.div`
    padding: 14px 0 14px 14px;
    background: transparent;
    border-radius: 0;
    margin-bottom: 0;
    border-left: 2px solid #e0e0e0;
    transition: all 0.2s ease;
    animation: ${fadeIn} 0.5s ease;
    font-size: 15px;
    line-height: 1.7;
    color: #555;
    box-sizing: border-box;
    width: 100%;
    word-wrap: break-word;

    &:hover {
        border-left-color: #F16622;
        padding-left: 18px;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const ConcernItem = styled(InsightItem)`
    border-left-color: #f0f0f0;
    box-sizing: border-box;

    &:hover {
        border-left-color: #faad14;
    }
`;

// Animated Loading
const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px 16px;
`;

const PulsingDot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%);
    animation: ${pulse} 1.5s ease-in-out infinite;
    box-shadow: 0 0 20px rgba(241, 102, 34, 0.5);
`;

const LoadingText = styled.div`
    margin-top: 24px;
    text-align: center;

    .status {
        font-weight: 600;
        font-size: 16px;
        background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin-bottom: 8px;
    }

    .message {
        font-size: 13px;
        color: #666;
    }
`;

// Professional Footer
const ActionBar = styled.div`
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, #f8f8f8 0%, #ffffff 100%);
    padding: 14px 16px;
    border-top: 2px solid #F16622;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
    display: flex;
    gap: 12px;
    justify-content: space-between;
    align-items: center;
    border-radius: 0;
    margin-top: auto;
`;

const GradientButton = styled(Button)`
    background: #F16622;
    border: none;
    color: white;
    height: 34px;
    border-radius: 0;
    font-weight: 600;
    font-size: 13px;
    padding: 0 18px;
    box-shadow: none;
    transition: all 0.2s ease;

    &:hover {
        background: #D14D00;
        color: white;
        transform: none;
        box-shadow: none;
    }

    &:active {
        transform: none;
    }

    &:disabled {
        background: #d9d9d9;
        color: #ffffff;
    }
`;

const OutlineButton = styled(Button)`
    border: 1px solid #F16622;
    color: #F16622;
    height: 34px;
    border-radius: 0;
    font-weight: 600;
    font-size: 13px;
    padding: 0 18px;
    background: white;
    transition: all 0.2s ease;

    &:hover {
        background: #F16622;
        color: white;
        border-color: #F16622;
        transform: none;
        box-shadow: none;
    }
`;

const StatusContainer = styled.div`
    text-align: center;
    padding: 30px 16px;
`;

const StreamingContainer = styled.div`
    background: transparent;
    border-radius: 0;
    padding: 16px 0;
    font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
    font-size: 12px;
    white-space: pre-wrap;
    max-height: 300px;
    overflow-y: auto;
    border-left: 2px solid #e0e0e0;
    padding-left: 12px;
    line-height: 1.6;
    color: #555;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
        background: #999;
        border-radius: 2px;
    }
`;

// Table Styles
const CustomSectionTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-top: 8px;

    th {
        background: transparent;
        padding: 10px 12px;
        text-align: left;
        border: none;
        border-bottom: 2px solid #e0e0e0;
        font-weight: 600;
        color: #333;
    }

    td {
        padding: 10px 12px;
        border: none;
        border-bottom: 1px solid #f5f5f5;
        color: #666;
    }

    tr:hover {
        background: rgba(241, 102, 34, 0.02);
    }
`;

const CustomSectionNarrative = styled.div`
    padding: 14px 0 14px 12px;
    background: transparent;
    border-radius: 0;
    line-height: 1.7;
    color: #555;
    border-left: 2px solid #e0e0e0;
    margin-top: 8px;
    transition: all 0.2s ease;

    &:hover {
        border-left-color: #F16622;
        padding-left: 16px;
    }
`;

const CustomSectionBreakdown = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;
`;

const BreakdownItem = styled.div`
    padding: 12px 0;
    background: transparent;
    border-radius: 0;
    border-bottom: 1px solid #f5f5f5;
    transition: all 0.2s ease;

    &:last-child {
        border-bottom: none;
    }

    .category {
        font-weight: 600;
        color: #333;
        font-size: 13px;
    }

    .value {
        color: #F16622;
        font-weight: 600;
        margin-left: 10px;
        font-size: 14px;
    }

    .percentage {
        color: #999;
        margin-left: 6px;
        font-size: 12px;
    }

    .insight {
        font-size: 12px;
        color: #666;
        margin-top: 6px;
        line-height: 1.5;
    }
`;

const CustomSectionList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 8px 0 0 0;

    li {
        padding: 10px 0 10px 12px;
        background: transparent;
        border-radius: 0;
        margin-bottom: 0;
        border-left: 2px solid #e0e0e0;
        border-bottom: 1px solid #f5f5f5;
        transition: all 0.2s ease;
        font-size: 14px;
        line-height: 1.6;
        color: #555;

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            border-left-color: #52c41a;
            padding-left: 16px;
        }

        &:before {
            content: "✓";
            color: #999;
            font-weight: normal;
            margin-right: 10px;
            font-size: 14px;
        }
    }
`;

const CustomSectionTimeline = styled.div`
    position: relative;
    padding-left: 0;
    margin-top: 8px;
`;

const CustomTimelineItem = styled.div`
    position: relative;
    padding: 10px 0 10px 12px;
    margin-bottom: 0;
    border-left: 2px solid #e0e0e0;
    border-bottom: 1px solid #f5f5f5;
    transition: all 0.2s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        border-left-color: #F16622;
        padding-left: 16px;
    }

    .date {
        font-weight: 600;
        color: #999;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
    }

    .event {
        color: #555;
        font-size: 14px;
        line-height: 1.6;
    }
`;

/**
 * AutoSummaryPanel Component
 */
const AutoSummaryPanel = ({
    report,
    datasetId,
    organizationId,
    moduleName,
    embedUrl,
    accessToken,
    onClose,
    onRegenerate,
    autoLoad = false,
    skipCache = false,
    showDashboard = false,
    onDashboardChange,
    isExpanded = false
}) => {
    const { instance, accounts } = useMsal();

    const {
        status,
        summary,
        streamingContent,
        error,
        fromCache,
        statusMessage,
        metadata,
        isLoading,
        isComplete,
        isError,
        canSubmitFeedback,
        generateSummary,
        cancelGeneration,
        reset,
        sendFeedback,
        regenerate,
        checkCache
    } = useReportSummary({
        organizationId,
        datasetId,
        moduleName,
        embedUrl,
        accessToken,
        autoCheckCache: !skipCache
    });

    const [feedbackError, setFeedbackError] = useState(null);
    const [cacheChecked, setCacheChecked] = useState(false);

    // Dashboard state - now controlled by parent
    const [dashboardSpec, setDashboardSpec] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [dashboardError, setDashboardError] = useState(null);
    const [dashboardCacheMetadata, setDashboardCacheMetadata] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [activeSections, setActiveSections] = useState([]);

    const getSectionKeys = React.useCallback((summaryData) => {
        if (!summaryData) return [];
        const keys = [];
        if (summaryData.keyMetrics && summaryData.keyMetrics.length > 0) keys.push('metrics');
        if (summaryData.keyInsights && summaryData.keyInsights.length > 0) keys.push('insights');
        if (summaryData.areasOfConcern && summaryData.areasOfConcern.length > 0) keys.push('concerns');
        if (summaryData.recommendations && summaryData.recommendations.length > 0) keys.push('recommendations');
        if (summaryData.customSections && summaryData.customSections.length > 0) {
            summaryData.customSections.forEach((_, index) => keys.push(index.toString()));
        }
        return keys;
    }, []);

    const allSectionKeys = React.useMemo(() => getSectionKeys(summary), [getSectionKeys, summary]);

    React.useEffect(() => {
        setActiveSections([]);
    }, [summary]);

    // Template system state (Phase 2)
    const [viewMode, setViewMode] = useState('text'); // 'text' | 'template'
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [templateFeedbackVisible, setTemplateFeedbackVisible] = useState(false);
    const [templateMetadata, setTemplateMetadata] = useState(null);

    // Auto-load from cache when drawer opens OR force generate if skipCache is true
    React.useEffect(() => {
        const loadOrGenerate = async () => {
            if (report && !cacheChecked) {
                setCacheChecked(true);

                try {
                    const pages = await report.getPages();
                    const activePage = pages.find(p => p.isActive);
                    const reportId = report.config?.id;
                    const pageId = activePage?.name;

                    if (reportId && pageId) {
                        if (skipCache) {
                            // Force fresh generation, skip cache
                            console.log('[AutoSummaryPanel] skipCache=true, forcing fresh generation...');
                            await generateSummary(report, { skipCache: true });
                        } else if (autoLoad) {
                            // Normal cache check
                            console.log('[AutoSummaryPanel] Auto-loading cached summary...');
                            await checkCache(reportId, pageId);
                        }
                    }
                } catch (error) {
                    console.warn('[AutoSummaryPanel] Load/generate failed:', error);
                }
            }
        };

        loadOrGenerate();
    }, [autoLoad, skipCache, report, cacheChecked, checkCache, generateSummary]);

    const handleGenerate = () => {
        generateSummary(report);
    };

    const handleRegenerate = () => {
        generateSummary(report, { skipCache: true }); // Pass skipCache=true for regeneration
    };

    const handleFeedback = async (helpful, rating, comment) => {
        try {
            setFeedbackError(null);
            await sendFeedback(helpful, rating, comment);
        } catch (err) {
            setFeedbackError(err.message);
        }
    };

    // Load dashboard function
    const loadDashboard = async () => {
        try {
            setDashboardLoading(true);
            setDashboardError(null);
            message.info('Generating AI-powered dashboard visualization...');

            // Get report and page info
            const pages = await report.getPages();
            const activePage = pages.find(p => p.isActive);
            const reportId = report.config?.id;
            const pageId = activePage?.name;

            console.log('[AutoSummaryPanel] Report object:', {
                reportId,
                pageId,
                hasReport: !!report,
                pagesCount: pages?.length,
                activePageName: activePage?.name,
                activePageDisplayName: activePage?.displayName
            });

            if (!reportId || !pageId) {
                throw new Error(`Could not determine report or page ID. ReportId: ${reportId}, PageId: ${pageId}`);
            }

            // Acquire API access token (not Power BI embed token)
            console.log('[AutoSummaryPanel] Acquiring API access token...');
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const apiAccessToken = tokenResponse.accessToken;
            console.log(`[AutoSummaryPanel] API token acquired, length: ${apiAccessToken?.length}`);

            console.log(`[AutoSummaryPanel] Loading dashboard for report: ${reportId}, page: ${pageId}`);

            // Call API to generate dashboard with correct API token
            const result = await generatePageDashboard(reportId, pageId, apiAccessToken);

            console.log('[AutoSummaryPanel] Dashboard API response:', result);

            if (result.success && result.data) {
                setDashboardSpec(result.data);
                setDashboardCacheMetadata(result.cache || null);
                console.log('[AutoSummaryPanel] Dashboard loaded successfully', {
                    isFromCache: result.cache?.isFromCache,
                    cacheAge: result.cache?.cacheAge
                });

                if (result.cache?.isFromCache) {
                    message.success(`Dashboard loaded from cache (${Math.floor(result.cache.cacheAge / 60)}m old)`);
                } else {
                    message.success('Executive dashboard generated successfully!');
                }
            } else {
                throw new Error(result.error || 'Failed to generate dashboard');
            }
        } catch (error) {
            console.error('[AutoSummaryPanel] Error loading dashboard:', error);
            const errorMessage = error.message || 'Failed to load dashboard';
            setDashboardError(`${errorMessage}. Please ensure you have generated a page summary first.`);
            message.error('Failed to load dashboard');
            // Turn off dashboard on error
            if (onDashboardChange) {
                onDashboardChange(false);
            }
        } finally {
            setDashboardLoading(false);
        }
    };

    // Message Listener for Iframe Templates
    React.useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === 'toggle-width') {
                if (onDashboardChange) onDashboardChange(event.data.expanded);
            }
            if (event.data?.type === 'regenerate') {
                handleRegenerate();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onDashboardChange, report]);

    // Load dashboard when showDashboard prop changes to true
    React.useEffect(() => {
        if (showDashboard && !dashboardSpec && !dashboardLoading && !dashboardError) {
            console.log('[AutoSummaryPanel] showDashboard=true, loading dashboard...');
            loadDashboard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDashboard, dashboardSpec, dashboardLoading, dashboardError]);

    // Memoize template report data to avoid unnecessary re-renders of the TemplateRenderer (Phase 2)
    const templateReportData = React.useMemo(() => {
        if (!summary) return null;
        return {
            summary: summary,
            visuals: summary.visualsExtracted || [],
            metrics: summary.keyMetrics || [],
            insights: summary.keyInsights || [],
            concerns: summary.areasOfConcern || [],
            recommendations: summary.recommendations || [],
            displayMode: (isExpanded || showDashboard) ? 'expanded' : 'compact', // Display mode for template rendering
            context: {
                reportId: report?.config?.id,
                organizationId: organizationId,
                fromCache: fromCache,
                metadata: metadata
            }
        };
    }, [summary, report, organizationId, fromCache, metadata, showDashboard]);

    // Load available templates for template system (Phase 2)
    useEffect(() => {
        const USE_TEMPLATE_SYSTEM = process.env.REACT_APP_USE_TEMPLATE_SYSTEM === 'true';

        if (USE_TEMPLATE_SYSTEM && accessToken) {
            loadAvailableTemplates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    const loadAvailableTemplates = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const result = await execute('GET', `${apiUrl}/v1/templates?analysisLevel=page&isActive=true`);

            if (result.success && result.data && result.data.length > 0) {
                setAvailableTemplates(result.data);
                setSelectedTemplate(result.data[0].templateId);
                console.log('[AutoSummaryPanel] Loaded templates:', result.data.length);
            }
        } catch (error) {
            console.error('[AutoSummaryPanel] Failed to load templates:', error);
            // Silently fail - template system is optional
        }
    };

    const getTrendIcon = (trend) => {
        if (trend === 'improving') return <ArrowUpOutlined />;
        if (trend === 'declining') return <ArrowDownOutlined />;
        return <MinusOutlined />;
    };

    const getTimelineColor = (text) => {
        if (text.toLowerCase().includes('immediate')) return '#52c41a';
        if (text.toLowerCase().includes('medium')) return '#faad14';
        return '#ff4d4f';
    };

    const formatTableCell = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        return JSON.stringify(value);
    };

    const formatPlainText = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        if (value.title && value.description) {
            return `${value.title}: ${value.description}`;
        }
        if (value.description) {
            return value.description;
        }
        if (value.text) {
            return value.text;
        }
        return JSON.stringify(value);
    };

    const coerceTableContent = (content) => {
        if (!content) return null;

        if (Array.isArray(content) && content.length > 0) {
            const allArrays = content.every((row) => Array.isArray(row));
            if (allArrays) {
                const maxLen = Math.max(...content.map((row) => row.length));
                if (maxLen > 0) {
                    const headers = Array.from({ length: maxLen }, (_, i) => `Column ${i + 1}`);
                    const rows = content.map((row) =>
                        headers.map((_, idx) => formatTableCell(row[idx]))
                    );
                    return { headers, rows };
                }
            }

            const allObjects = content.every(
                (row) => row && typeof row === 'object' && !Array.isArray(row)
            );
            if (allObjects) {
                const headers = Array.from(
                    new Set(content.flatMap((row) => Object.keys(row)))
                );
                if (headers.length > 0) {
                    const rows = content.map((row) =>
                        headers.map((key) => formatTableCell(row[key]))
                    );
                    return { headers, rows };
                }
            }
        }

        if (content && typeof content === 'object' && !Array.isArray(content)) {
            const headers = content.headers || content.columns;
            const rows = content.rows || content.data || content.values;
            if (Array.isArray(headers) && Array.isArray(rows)) {
                const normalizedRows = rows.map((row) => {
                    if (Array.isArray(row)) {
                        return headers.map((_, idx) => formatTableCell(row[idx]));
                    }
                    if (row && typeof row === 'object') {
                        return headers.map((key) => formatTableCell(row[key]));
                    }
                    return headers.map((_, idx) => formatTableCell(row?.[idx]));
                });
                return { headers, rows: normalizedRows };
            }
        }

        return null;
    };

    const coerceKeyValueTable = (value) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
        const entries = Object.entries(value);
        if (!entries.length) return null;
        return {
            headers: ['Key', 'Value'],
            rows: entries.map(([key, val]) => [key, formatTableCell(val)])
        };
    };

    const parseJsonIfPossible = (value) => {
        if (typeof value !== 'string') return value;
        const trimmed = value.trim();
        if (!trimmed) return value;
        const looksJson = (trimmed.startsWith('{') && trimmed.endsWith('}'))
            || (trimmed.startsWith('[') && trimmed.endsWith(']'));
        if (!looksJson) return value;
        try {
            return JSON.parse(trimmed);
        } catch (error) {
            return value;
        }
    };

    const getTableFromValue = (value) => {
        const candidates = [];
        const pushCandidate = (candidate) => {
            if (candidate?.headers?.length && candidate?.rows?.length) {
                candidates.push(candidate);
            }
        };

        const normalized = parseJsonIfPossible(value);
        pushCandidate(coerceTableContent(normalized));
        pushCandidate(coerceKeyValueTable(normalized));

        if (Array.isArray(normalized)) {
            const parsedItems = normalized.map((item) => parseJsonIfPossible(item));
            pushCandidate(coerceTableContent(parsedItems));
            parsedItems.forEach((item) => {
                pushCandidate(coerceTableContent(item));
                pushCandidate(coerceKeyValueTable(item));
            });
        } else if (normalized && typeof normalized === 'object') {
            ['items', 'rows', 'data', 'values'].forEach((key) => {
                pushCandidate(coerceTableContent(normalized[key]));
                pushCandidate(coerceKeyValueTable(normalized[key]));
            });
        }

        if (!candidates.length) return null;
        candidates.sort((a, b) => (b.rows?.length || 0) - (a.rows?.length || 0));
        return candidates[0];
    };

    const coerceTableFromList = (items) => {
        if (!Array.isArray(items) || items.length === 0) return null;
        return getTableFromValue(items);
    };

    const renderTableContent = (tableContent, summaryText) => (
        <div>
            <CustomSectionTable>
                <thead>
                    <tr>
                        {tableContent.headers.map((header, i) => (
                            <th key={i}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {tableContent.rows.map((row, i) => (
                        <tr key={i}>
                            {row.map((cell, j) => (
                                <td key={j}>{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </CustomSectionTable>
            {summaryText && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block', fontStyle: 'italic' }}>
                    {summaryText}
                </Text>
            )}
        </div>
    );

    const renderCustomSection = (section, index) => {
        if (!section || !section.content) return null;

        const { title, type, content, priority } = section;
        const normalizedContent = parseJsonIfPossible(content);
        const summaryText = normalizedContent && typeof normalizedContent === 'object'
            ? normalizedContent.summary
            : undefined;
        let sectionContent;

        const hasDescriptionCards = normalizedContent
            && typeof normalizedContent === 'object'
            && (Object.prototype.hasOwnProperty.call(normalizedContent, 'description')
                || Object.prototype.hasOwnProperty.call(normalizedContent, 'cards'));

        if (hasDescriptionCards) {
            const cardsTable = getTableFromValue(normalizedContent.cards);
            const descriptionText = normalizedContent.description
                ? formatPlainText(normalizedContent.description)
                : '';
            sectionContent = (
                <div>
                    {descriptionText && (
                        <CustomSectionNarrative>
                            {descriptionText}
                        </CustomSectionNarrative>
                    )}
                    {cardsTable && renderTableContent(cardsTable)}
                </div>
            );
        } else {
            const genericTable = getTableFromValue(normalizedContent);
            if (genericTable) {
                sectionContent = renderTableContent(genericTable, summaryText);
            } else switch (type?.toLowerCase()) {
                case 'table':
                    if (normalizedContent?.headers && normalizedContent?.rows) {
                        sectionContent = renderTableContent(
                            {
                                headers: normalizedContent.headers,
                                rows: normalizedContent.rows.map((row) =>
                                    row.map((cell) => formatTableCell(cell))
                                ),
                            },
                            summaryText
                        );
                    } else {
                        const tableContent = getTableFromValue(normalizedContent);
                        sectionContent = tableContent
                            ? renderTableContent(tableContent)
                            : (
                                <pre style={{ fontSize: 12, background: 'transparent', padding: '14px 0 14px 12px', borderRadius: 0, overflowX: 'auto', borderLeft: '2px solid #e0e0e0', color: '#666' }}>
                                    {JSON.stringify(normalizedContent, null, 2)}
                                </pre>
                            );
                    }
                    break;

                case 'narrative':
                    sectionContent = (
                        <CustomSectionNarrative>
                            {normalizedContent?.text
                                || normalizedContent?.narrative
                                || formatPlainText(normalizedContent)}
                        </CustomSectionNarrative>
                    );
                    break;

                case 'breakdown':
                    if (normalizedContent?.items && Array.isArray(normalizedContent.items)) {
                        sectionContent = (
                            <CustomSectionBreakdown>
                                {normalizedContent.items.map((item, i) => (
                                    <BreakdownItem key={i}>
                                        <div>
                                            <span className="category">{item.category}</span>
                                            <span className="value">{item.value}</span>
                                            {item.percentage && (
                                                <span className="percentage">({item.percentage})</span>
                                            )}
                                        </div>
                                        {item.insight && (
                                            <div className="insight">{item.insight}</div>
                                        )}
                                    </BreakdownItem>
                                ))}
                                {summaryText && (
                                    <Text type="secondary" style={{ fontSize: 12, marginTop: 8, fontStyle: 'italic' }}>
                                        {summaryText}
                                    </Text>
                                )}
                            </CustomSectionBreakdown>
                        );
                    } else {
                        const tableContent = getTableFromValue(normalizedContent);
                        sectionContent = tableContent
                            ? renderTableContent(tableContent)
                            : (
                                <pre style={{ fontSize: 12, background: 'transparent', padding: '14px 0 14px 12px', borderRadius: 0, overflowX: 'auto', borderLeft: '2px solid #e0e0e0', color: '#666' }}>
                                    {JSON.stringify(normalizedContent, null, 2)}
                                </pre>
                            );
                    }
                    break;

                case 'list':
                    if (normalizedContent?.items && Array.isArray(normalizedContent.items)) {
                        sectionContent = (
                            <div>
                                <CustomSectionList>
                                    {normalizedContent.items.map((item, i) => (
                                        <li key={i}>{formatPlainText(item)}</li>
                                    ))}
                                </CustomSectionList>
                                {normalizedContent.note && (
                                    <Text type="secondary" style={{ fontSize: 12, marginTop: 10, display: 'block' }}>
                                        <strong>Note:</strong> {normalizedContent.note}
                                    </Text>
                                )}
                            </div>
                        );
                    } else {
                        const tableContent = getTableFromValue(normalizedContent);
                        sectionContent = tableContent
                            ? renderTableContent(tableContent)
                            : (
                                <pre style={{ fontSize: 12, background: 'transparent', padding: '14px 0 14px 12px', borderRadius: 0, overflowX: 'auto', borderLeft: '2px solid #e0e0e0', color: '#666' }}>
                                    {JSON.stringify(normalizedContent, null, 2)}
                                </pre>
                            );
                    }
                    break;

                case 'timeline':
                    if (normalizedContent?.data && Array.isArray(normalizedContent.data)) {
                        sectionContent = (
                            <CustomSectionTimeline>
                                {normalizedContent.data.map((item, i) => (
                                    <CustomTimelineItem key={i}>
                                        <div className="date">{item.date}</div>
                                        <div className="event">{item.event}</div>
                                    </CustomTimelineItem>
                                ))}
                            </CustomSectionTimeline>
                        );
                    } else {
                        const tableContent = getTableFromValue(normalizedContent);
                        sectionContent = tableContent
                            ? renderTableContent(tableContent)
                            : (
                                <pre style={{ fontSize: 12, background: 'transparent', padding: '14px 0 14px 12px', borderRadius: 0, overflowX: 'auto', borderLeft: '2px solid #e0e0e0', color: '#666' }}>
                                    {JSON.stringify(normalizedContent, null, 2)}
                                </pre>
                            );
                    }
                    break;

                default:
                    {
                        sectionContent = (
                            <pre style={{ fontSize: 12, background: 'transparent', padding: '14px 0 14px 12px', borderRadius: 0, overflowX: 'auto', borderLeft: '2px solid #e0e0e0', color: '#666' }}>
                                {JSON.stringify(normalizedContent, null, 2)}
                            </pre>
                        );
                    }
            }
        }

        const priorityColor = priority === 'high' ? '#ff4d4f' : priority === 'medium' ? '#faad14' : '#52c41a';

        return (
            <Panel
                key={index}
                header={
                    <SectionHeader>
                        <div className="title">
                            <InfoCircleOutlined className="icon" style={{ color: '#F16622' }} />
                            {title}
                        </div>
                        {priority && (
                            <Tag color={priorityColor} style={{ marginRight: 0 }}>
                                {priority.toUpperCase()}
                            </Tag>
                        )}
                    </SectionHeader>
                }
            >
                {sectionContent}
            </Panel>
        );
    };

    const renderStatus = () => {
        // Show dashboard if enabled
        if (showDashboard && dashboardSpec) {
            return <AIDashboardReport dashboardSpec={dashboardSpec} />;
        }

        // Show dashboard error if any
        if (dashboardError) {
            return (
                <StatusContainer>
                    <Alert
                        type="error"
                        message="Error Loading Dashboard"
                        description={dashboardError}
                        showIcon
                        style={{ marginBottom: 24, borderRadius: 0 }}
                    />
                    <OutlineButton onClick={() => onDashboardChange && onDashboardChange(false)}>
                        Back to Summary
                    </OutlineButton>
                </StatusContainer>
            );
        }

        switch (status) {
            case SUMMARY_STATUS.IDLE:
                return (
                    <StatusContainer>
                        <div style={{ marginBottom: 24 }}>
                            <RobotOutlined style={{ fontSize: 64, background: 'linear-gradient(135deg, #FF7F3F 0%, #F16622 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
                        </div>
                        <Title level={4} style={{ marginBottom: 12 }}>AI Report Summary</Title>
                        <Paragraph type="secondary" style={{ maxWidth: 400, margin: '0 auto 24px' }}>
                            Get an AI-powered summary of your report data including key metrics,
                            insights, and recommendations powered by <span style={{ color: '#1890ff', fontWeight: 600 }}>Gemini | BIC Engine</span>.
                        </Paragraph>
                        <GradientButton
                            size="large"
                            icon={<ThunderboltOutlined />}
                            onClick={handleGenerate}
                        >
                            Generate Summary
                        </GradientButton>
                    </StatusContainer>
                );

            case SUMMARY_STATUS.CHECKING_CACHE:
            case SUMMARY_STATUS.EXTRACTING:
            case SUMMARY_STATUS.SANITIZING:
            case SUMMARY_STATUS.GENERATING:
                return (
                    <LoadingContainer>
                        <Space direction="vertical" align="center" size="large">
                            <PulsingDot />
                            <LoadingText>
                                <div className="status">{statusMessage || 'Processing...'}</div>
                                <div className="message">This may take a few moments</div>
                            </LoadingText>
                        </Space>
                        <OutlineButton
                            onClick={cancelGeneration}
                            style={{ marginTop: 24 }}
                        >
                            Cancel
                        </OutlineButton>
                    </LoadingContainer>
                );

            case SUMMARY_STATUS.STREAMING:
                return (
                    <div style={{ padding: '24px' }}>
                        <Space style={{ marginBottom: 16 }}>
                            <Spin size="small" />
                            <Text strong style={{ fontSize: 15 }}>Generating summary...</Text>
                        </Space>
                        <StreamingContainer>
                            {streamingContent || 'Waiting for response...'}
                        </StreamingContainer>
                    </div>
                );

            case SUMMARY_STATUS.ERROR:
                return (
                    <StatusContainer>
                        <Alert
                            type="error"
                            message="Error Generating Summary"
                            description={error?.message || 'An unexpected error occurred'}
                            showIcon
                            style={{ marginBottom: 24, borderRadius: 0 }}
                        />
                        <GradientButton
                            icon={<ReloadOutlined />}
                            onClick={handleGenerate}
                        >
                            Try Again
                        </GradientButton>
                    </StatusContainer>
                );

            case SUMMARY_STATUS.COMPLETE:
                return renderSummary();

            default:
                return null;
        }
    };

    const renderSummary = () => {
        if (!summary) return null;

        const insightCount = summary.keyInsights?.length || 0;
        const metricCount = summary.keyMetrics?.length || 0;
        const concernCount = summary.areasOfConcern?.length || 0;

        // Build default active keys to include all sections (including custom sections)
        const defaultKeys = ['metrics', 'insights', 'concerns', 'recommendations'];
        if (summary.customSections && summary.customSections.length > 0) {
            // Add custom section indices to default active keys
            summary.customSections.forEach((_, index) => defaultKeys.push(index.toString()));
        }

        const USE_TEMPLATE_SYSTEM = process.env.REACT_APP_USE_TEMPLATE_SYSTEM === 'true';

        // Compact Template Controls - Removed Segmented toggle as per request
        const templateControls = USE_TEMPLATE_SYSTEM && availableTemplates.length > 0 && viewMode === 'template' && (
            <Space size="middle">
                <Select
                    size="small"
                    value={selectedTemplate}
                    onChange={setSelectedTemplate}
                    style={{ width: 180 }}
                    placeholder="Select Template"
                    options={availableTemplates.map(t => ({
                        label: t.templateName,
                        value: t.templateId
                    }))}
                />
            </Space>
        );

        return (
            <>
                {/* Conditional Rendering: Template or Text Summary */}
                {viewMode === 'template' && USE_TEMPLATE_SYSTEM && selectedTemplate ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <TemplateRenderer
                            templateId={selectedTemplate}
                            reportData={templateReportData}
                            controls={templateControls}
                            onFeedback={(meta) => {
                                setTemplateMetadata(meta);
                                setTemplateFeedbackVisible(true);
                            }}
                        />
                        {templateFeedbackVisible && (
                            <TemplateFeedbackModal
                                visible={templateFeedbackVisible}
                                onClose={() => setTemplateFeedbackVisible(false)}
                                metadata={templateMetadata}
                                summaryId={metadata?.summaryId}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {/* Remove toggle bar completely for cleaner look */}
                        {/* ══════════════════════════════════════════════════════════════════════
                            HYBRID RENDERER: Standard JSON (Headline) or Template HTML
                        ══════════════════════════════════════════════════════════════════════ */}
                        {summary.isHtml ? (
                            <div
                                className="ai-report-content"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    background: '#fff',
                                    height: '100%',
                                    width: '100%',
                                    overflow: 'hidden',
                                    padding: 0,
                                    margin: 0
                                }}
                            >
                                <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <iframe
                                        title="Page Summary Report"
                                         srcDoc={(function() {
                                            const html = summary.htmlContent;
                                             const styleInjection = `
                                            <style>
                                                .header-bar, .footer-bar, .ui-controls { display: none !important; }
                                                .content-wrapper, .main-wrapper { padding-top: 10px !important; padding-bottom: 20px !important; max-width: none !important; width: 100% !important; }
                                                body, .drawer-mode, .wide-mode { 
                                                    height: auto !important; 
                                                    min-height: 100% !important; 
                                                    max-width: none !important; 
                                                    width: 100% !important; 
                                                    margin: 0 !important; 
                                                    padding: 0 !important;
                                                    background: white !important;
                                                }
                                                .visual-tab, .exec-tab, .dashboard-container { max-width: none !important; width: 100% !important; padding: 10px !important; }
                                                ::-webkit-scrollbar { width: 6px; }
                                                ::-webkit-scrollbar-track { background: transparent; }
                                                ::-webkit-scrollbar-thumb { background: #e8e8e8; border-radius: 10px; }
                                            </style>
                                            `;
                                            const interactiveScript = `
                                            <script>
                                                (function() {
                                                    const expanded = ${isExpanded || showDashboard};
                                                    console.log('[AutoSummary] Internal Template Script Initialized. Expanded:', expanded);
                                                    
                                                    // Set Initial Mode based on Parent State
                                                    if (expanded) {
                                                        document.body.classList.remove('drawer-mode');
                                                        document.body.classList.add('wide-mode');
                                                        const tb = document.getElementById('toggleMode');
                                                        if (tb) tb.innerText = "Switch to Drawer Mode";
                                                    } else {
                                                        document.body.classList.remove('wide-mode');
                                                        document.body.classList.add('drawer-mode');
                                                        const tb = document.getElementById('toggleMode');
                                                        if (tb) tb.innerText = "Switch to Wide Mode";
                                                    }

                                                    // Tab Switching Implementation
                                                    window.switchTab = function(tab) {
                                                        const vTab = document.getElementById('visual-tab');
                                                        const eTab = document.getElementById('exec-tab');
                                                        const btns = document.querySelectorAll('.tab-btn');
                                                        if (vTab && eTab) {
                                                            if (tab === 'visual') {
                                                                vTab.classList.remove('hidden');
                                                                eTab.classList.add('hidden');
                                                                if (btns[0]) btns[0].classList.add('active');
                                                                if (btns[1]) btns[1].classList.remove('active');
                                                            } else {
                                                                vTab.classList.add('hidden');
                                                                eTab.classList.remove('hidden');
                                                                if (btns[1]) btns[1].classList.add('active');
                                                                if (btns[0]) btns[0].classList.remove('active');
                                                            }
                                                        }
                                                    };

                                                    // Toggle Mode (Drawer vs Wide) Implementation
                                                    const toggleBtn = document.getElementById('toggleMode');
                                                    if (toggleBtn) {
                                                        toggleBtn.onclick = function() {
                                                            const isWide = document.body.classList.contains('wide-mode');
                                                            if (isWide) {
                                                                document.body.classList.remove('wide-mode');
                                                                document.body.classList.add('drawer-mode');
                                                                toggleBtn.innerText = "Switch to Wide Mode";
                                                                window.parent.postMessage({ type: 'toggle-width', expanded: false }, '*');
                                                                window.switchTab('visual');
                                                            } else {
                                                                document.body.classList.remove('drawer-mode');
                                                                document.body.classList.add('wide-mode');
                                                                toggleBtn.innerText = "Switch to Drawer Mode";
                                                                window.parent.postMessage({ type: 'toggle-width', expanded: true }, '*');
                                                            }
                                                        };
                                                    }
                                                    
                                                    // Global Regenerate Implementation
                                                    window.regenerateDigest = function() {
                                                        window.parent.postMessage({ type: 'regenerate' }, '*');
                                                    };
                                                })();
                                            </script>
                                            `;
                                            let finalHtml = html;
                                            if (finalHtml.includes('</head>')) {
                                                finalHtml = finalHtml.replace('</head>', styleInjection + '</head>');
                                            } else {
                                                finalHtml = styleInjection + finalHtml;
                                            }
                                            if (finalHtml.includes('</body>')) {
                                                return finalHtml.replace('</body>', interactiveScript + '</body>');
                                            }
                                            return finalHtml + interactiveScript;
                                        })()}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            display: 'block'
                                        }}
                                        sandbox="allow-scripts allow-same-origin allow-popups"
                                    />
                                </div>
                                
                                {/* Page Summary Footer Removed - Handled by Parent Drawer */}
                            </div>
                        ) : (
                            <HeadlineSection>
                                {/* Headline Section with Quick Stats */}
                                <HeadlineRow>
                                    <HeadlineText>{summary.headline}</HeadlineText>
                                </HeadlineRow>

                                {/* Data Source Indicators */}
                                <Space size="small" wrap style={{ marginBottom: 12 }}>
                                    {summary.dataSources?.usedScreenshot && (
                                        <DataSourceBadge>
                                            <CameraOutlined className="icon" />
                                            Visual Analysis
                                        </DataSourceBadge>
                                    )}
                                    {summary.dataSources?.usedStructuredData && (
                                        <DataSourceBadge>
                                            <DatabaseOutlined className="icon" />
                                            Structured Data
                                        </DataSourceBadge>
                                    )}
                                    {summary.dataSources?.confidence && (
                                        <DataSourceBadge>
                                            <TrophyOutlined className="icon" />
                                            {summary.dataSources.confidence.charAt(0).toUpperCase() + summary.dataSources.confidence.slice(1)} Confidence
                                        </DataSourceBadge>
                                    )}
                                    {fromCache && (
                                        <DataSourceBadge>
                                            <ClockCircleOutlined className="icon" />
                                            Cached
                                        </DataSourceBadge>
                                    )}
                                </Space>

                                <QuickStats>
                                    <StatBox>
                                        <div className="label">Insights</div>
                                        <div className="value">{insightCount}</div>
                                    </StatBox>
                                    <StatBox>
                                        <div className="label">Metrics</div>
                                        <div className="value">{metricCount}</div>
                                    </StatBox>
                                    <StatBox>
                                        <div className="label">Concerns</div>
                                        <div className="value">{concernCount}</div>
                                    </StatBox>
                                </QuickStats>
                            </HeadlineSection>
                        )}
                        {/* Collapsible Sections - Only shown in Text Mode without HTML content */}
                        {!summary.isHtml && (
                            <>
                                <SectionToggleBar>
                                    <Tooltip title={activeSections.length === allSectionKeys.length ? "Collapse all sections" : "Expand all sections"}>
                                        <Button
                                            type="text"
                                            icon={
                                                <DoubleRightOutlined
                                                    style={{
                                                        transform: activeSections.length === allSectionKeys.length ? 'rotate(90deg)' : 'none',
                                                        transition: 'transform 0.15s ease'
                                                    }}
                                                />
                                            }
                                            onClick={() => {
                                                if (activeSections.length === allSectionKeys.length) {
                                                    setActiveSections([]);
                                                } else {
                                                    setActiveSections(allSectionKeys);
                                                }
                                            }}
                                            disabled={allSectionKeys.length === 0}
                                            style={{ color: '#F16622', fontSize: 16, marginRight: -4 }}
                                        />
                                    </Tooltip>
                                </SectionToggleBar>

                        {/* Collapsible Sections */}
                        <StyledCollapse
                            activeKey={activeSections}
                            onChange={(keys) => {
                                const nextKeys = Array.isArray(keys) ? keys : keys ? [keys] : [];
                                setActiveSections(nextKeys);
                            }}
                            ghost
                            expandIconPosition="end"
                        >
                            {/* Key Metrics */}
                            {summary.keyMetrics && summary.keyMetrics.length > 0 && (
                                <Panel
                                    header={
                                        <SectionHeader>
                                            <div className="title">
                                                <TrophyOutlined className="icon" style={{ color: '#F16622' }} />
                                                Key Metrics
                                            </div>
                                            <Badge count={summary.keyMetrics.length} style={{ background: 'linear-gradient(135deg, #FF7F3F 0%, #F16622 100%)' }} />
                                        </SectionHeader>
                                    }
                                    key="metrics"
                                >
                                    {summary.keyMetrics.map((metric, index) => (
                                        <MetricCard key={index}>
                                            <MetricHeader>
                                                <div style={{ flex: 1 }}>
                                                    <MetricName>{metric.name || metric.metric}</MetricName>
                                                    <TrendBadge className={metric.trend || 'stable'}>
                                                        {getTrendIcon(metric.trend)}
                                                        {metric.trend || 'stable'}
                                                    </TrendBadge>
                                                </div>
                                                <MetricValue>{metric.value}</MetricValue>
                                            </MetricHeader>
                                            {metric.analysis && (
                                                <Text type="secondary" style={{ fontSize: 13, display: 'block', lineHeight: 1.6 }}>
                                                    {metric.analysis}
                                                </Text>
                                            )}
                                            {metric.value && (
                                                <MetricProgress>
                                                    <div className="fill" style={{ width: `${Math.min(100, parseFloat(metric.value) || 75)}%` }} />
                                                </MetricProgress>
                                            )}
                                        </MetricCard>
                                    ))}
                                </Panel>
                            )
                            }

                            {/* Key Insights */}
                            {
                                summary.keyInsights && summary.keyInsights.length > 0 && (
                                    <Panel
                                        header={
                                            <SectionHeader>
                                                <div className="title">
                                                    <BulbOutlined className="icon" style={{ color: '#52c41a' }} />
                                                    Key Insights
                                                </div>
                                                <Badge count={summary.keyInsights.length} style={{ background: '#52c41a' }} />
                                            </SectionHeader>
                                        }
                                        key="insights"
                                    >
                                        {(() => {
                                            const tableContent = coerceTableFromList(summary.keyInsights);
                                            if (tableContent) {
                                                return renderTableContent(tableContent);
                                            }
                                            return summary.keyInsights.map((insight, index) => {
                                                // Handle both string format and object format
                                                let text;
                                                if (typeof insight === 'string') {
                                                    text = insight;
                                                } else if (insight.title && insight.description) {
                                                    text = `${insight.title}: ${insight.description}`;
                                                } else if (insight.description) {
                                                    text = insight.description;
                                                } else if (insight.insight) {
                                                    text = insight.insight;
                                                } else {
                                                    text = JSON.stringify(insight);
                                                }
                                                return (
                                                    <InsightItem key={index}>
                                                        {text}
                                                    </InsightItem>
                                                );
                                            });
                                        })()}
                                    </Panel>
                                )
                            }

                            {/* Areas of Concern */}
                            {
                                summary.areasOfConcern && summary.areasOfConcern.length > 0 && (
                                    <Panel
                                        header={
                                            <SectionHeader>
                                                <div className="title">
                                                    <WarningOutlined className="icon" style={{ color: '#faad14' }} />
                                                    Areas of Concern
                                                </div>
                                                <Badge count={summary.areasOfConcern.length} style={{ background: '#faad14' }} />
                                            </SectionHeader>
                                        }
                                        key="concerns"
                                    >
                                        {(() => {
                                            const tableContent = coerceTableFromList(summary.areasOfConcern);
                                            if (tableContent) {
                                                return renderTableContent(tableContent);
                                            }
                                            return summary.areasOfConcern.map((concern, index) => {
                                                const text = typeof concern === 'string'
                                                    ? concern
                                                    : (concern.concern || JSON.stringify(concern));
                                                return (
                                                    <ConcernItem key={index}>
                                                        {text}
                                                    </ConcernItem>
                                                );
                                            });
                                        })()}
                                    </Panel>
                                )
                            }

                            {/* Considerations - Timeline Style */}
                            {
                                summary.recommendations && summary.recommendations.length > 0 && (
                                    <Panel
                                        header={
                                            <SectionHeader>
                                                <div className="title">
                                                    <CheckCircleOutlined className="icon" style={{ color: '#1890ff' }} />
                                                    Considerations
                                                </div>
                                                <Badge count={summary.recommendations.length} style={{ background: '#1890ff' }} />
                                            </SectionHeader>
                                        }
                                        key="recommendations"
                                    >
                                        {(() => {
                                            const tableContent = coerceTableFromList(summary.recommendations);
                                            if (tableContent) {
                                                return renderTableContent(tableContent);
                                            }
                                            return (
                                                <Timeline>
                                                    {summary.recommendations.map((rec, index) => {
                                                        const text = typeof rec === 'string' ? rec : (rec.recommendation || JSON.stringify(rec));
                                                        const color = getTimelineColor(text);
                                                        const bgColor = color === '#52c41a' ? '#f6ffed' : color === '#faad14' ? '#fffbe6' : '#fff1f0';

                                                        let badgeText = 'ACTION';
                                                        if (text.toLowerCase().includes('immediate')) badgeText = 'Immediate (0-30 days)';
                                                        else if (text.toLowerCase().includes('medium')) badgeText = 'Medium (30-90 days)';
                                                        else if (text.toLowerCase().includes('strategic')) badgeText = 'Strategic (90+ days)';

                                                        return (
                                                            <TimelineItem key={index} color={color}>
                                                                <TimelineBadge bg={bgColor} color={color}>
                                                                    {badgeText}
                                                                </TimelineBadge>
                                                                <TimelineContent borderColor={color}>
                                                                    {text.replace(/^(Immediate|Medium|Strategic)\s*\([^)]+\):\s*/i, '')}
                                                                </TimelineContent>
                                                            </TimelineItem>
                                                        );
                                                    })}
                                                </Timeline>
                                            );
                                        })()}
                                    </Panel>
                                )
                            }

                            {/* Custom Sections */}
                            {
                                summary.customSections && summary.customSections.length > 0 && (
                                    summary.customSections.map((section, index) => renderCustomSection(section, index))
                                )
                            }
                        </StyledCollapse >

                        {/* Feedback */}
                        < div style={{ marginTop: 24 }}>
                            <FeedbackWidget
                                onFeedback={handleFeedback}
                                disabled={!canSubmitFeedback}
                                error={feedbackError}
                            />
                        </div >

                        {/* Metadata */}
                        {
                            metadata && (
                                <div style={{ marginTop: 16, padding: '12px 0', background: 'transparent', borderTop: '1px solid #f0f0f0', fontSize: 11, color: '#999' }}>
                                    {metadata.tokensUsed && (
                                        <span>Tokens: {metadata.tokensUsed} | </span>
                                    )}
                                    {metadata.generatedAt && (
                                        <span>{new Date(metadata.generatedAt).toLocaleString()}</span>
                                    )}
                                </div>
                            )
                        }
                            </>
                        )}
                    </>
                )}
            </>
        );
    };

    // Regenerate dashboard handler
    const handleRegenerateDashboard = async () => {
        try {
            setIsRegenerating(true);
            message.info('Regenerating dashboard with latest data...');

            const pages = await report.getPages();
            const activePage = pages.find(p => p.isActive);
            const reportId = report.config?.id;
            const pageId = activePage?.name;

            console.log('[AutoSummaryPanel] Report object:', {
                id: reportId,
                activePage: pageId
            });

            const tokenResponse = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });
            const apiAccessToken = tokenResponse.accessToken;

            const result = await regeneratePageDashboard(reportId, pageId, apiAccessToken); // Assuming regeneratePageDashboard is the correct function

            if (result.success && result.data) {
                setDashboardSpec(result.data);
                setDashboardCacheMetadata(result.cache || null);
                message.success('Dashboard regenerated successfully!'); // Changed message to reflect regeneration
            } else if (result.notFound) {
                message.warning('No digest available. Please generate a digest first.');
                if (onDashboardChange) onDashboardChange(false);
            } else {
                // Original logic for regeneration error
                throw new Error(result.error || 'Failed to regenerate dashboard');
            }
        } catch (error) {
            console.error('[AutoSummaryPanel] Error regenerating dashboard:', error);
            message.error('Failed to regenerate dashboard');
        } finally {
            setIsRegenerating(false);
        }
    };

    // If showing dashboard, render it directly with Tabs support
    if (showDashboard && dashboardSpec) {
        const renderDashboardContent = () => (
            <div style={{ height: 'calc(100vh - 46px)', overflowY: 'auto', background: '#fafafa' }}>
                <AIDashboardReport
                    dashboardSpec={dashboardSpec}
                    summary={summary}
                    cacheMetadata={dashboardCacheMetadata}
                    onRegenerate={handleRegenerateDashboard}
                    isRegenerating={isRegenerating}
                />
            </div>
        );

        const renderPremiumTemplate = () => (
            <div style={{ height: 'calc(100vh - 46px)', overflow: 'hidden' }}>
                {availableTemplates.length > 0 ? (
                    <TemplateRenderer
                        templateId={selectedTemplate || availableTemplates[0].templateId}
                        reportData={templateReportData}
                        hideHeader={true}
                        onFeedback={(meta) => {
                            setTemplateMetadata(meta);
                            setTemplateFeedbackVisible(true);
                        }}
                    />
                ) : (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <Text type="secondary">Premium analysis template not available for this report.</Text>
                    </div>
                )}
            </div>
        );

        // Determine tab structure based on template availability
        const tabItems = [
            {
                key: 'dashboard',
                label: (
                    <span>
                        <DashboardOutlined />
                        Analysis Dashboard
                    </span>
                ),
                children: renderDashboardContent()
            }
        ];

        // Add Premium Template tab if templates are available
        if (availableTemplates.length > 0) {
            tabItems.push({
                key: 'premium',
                label: (
                    <span>
                        <ThunderboltOutlined />
                        Premium Analysis
                    </span>
                ),
                children: renderPremiumTemplate()
            });
        }

        return (
            <div className="expanded-ai-container" style={{ height: '100vh', width: '100%', background: '#fff', position: 'fixed', top: 0, left: 0, zIndex: 1000 }}>
                <Tabs
                    defaultActiveKey="dashboard"
                    centered
                    tabBarStyle={{
                        background: '#fff',
                        margin: 0,
                        padding: '0 24px',
                        borderBottom: '1px solid #f0f0f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                    items={tabItems}
                />
                
                {templateFeedbackVisible && (
                    <TemplateFeedbackModal
                        visible={templateFeedbackVisible}
                        onClose={() => setTemplateFeedbackVisible(false)}
                        metadata={templateMetadata}
                        summaryId={metadata?.summaryId}
                    />
                )}

                {/* Close Button overlay */}
                <Button 
                    icon={<CloseOutlined />} 
                    onClick={() => onDashboardChange && onDashboardChange(false)}
                    style={{ position: 'fixed', top: 8, right: 16, zIndex: 1100, borderRadius: '50%', width: 32, height: 32, padding: 0 }}
                />
            </div>
        );
    }

    // If dashboard loading, show loading indicator
    if (showDashboard && dashboardLoading) {
        return (
            <LoadingContainer>
                <Space direction="vertical" align="center" size="large">
                    <PulsingDot />
                    <LoadingText>
                        <div className="status">Generating Dashboard...</div>
                        <div className="message">Creating executive visualization</div>
                    </LoadingText>
                </Space>
            </LoadingContainer>
        );
    }

    // If dashboard error, show error
    if (showDashboard && dashboardError) {
        return (
            <StatusContainer>
                <Alert
                    type="error"
                    message="Error Loading Dashboard"
                    description={dashboardError}
                    showIcon
                    style={{ marginBottom: 24, borderRadius: 0 }}
                />
                <OutlineButton onClick={() => onDashboardChange && onDashboardChange(false)}>
                    Back to Summary
                </OutlineButton>
            </StatusContainer>
        );
    }

    // For HTML templates, render edge-to-edge to occupy full available space
    if ((summary?.isHtml || isLoading) && !showDashboard) {
        return (
            <div style={{ 
                height: 'calc(100vh - 120px)', // Occupy space between header and footer
                width: '100%', 
                overflow: 'hidden', 
                background: '#fff',
                padding: 0,
                margin: 0
            }}>
                {renderStatus()}
            </div>
        );
    }

    // Otherwise, show normal summary in wrapped container
    return (
        <ModernPanelContainer>
            <InnerPanel>
                <ScrollableContent>
                    {renderStatus()}
                </ScrollableContent>
            </InnerPanel>
        </ModernPanelContainer>
    );
}

export default AutoSummaryPanel;
